import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import useSencoPupils from '../../hooks/useSencoPupils';
import { filterPupilsWithPanel, isSenPathwayEligible, sortPupilsBySurname, yearGroupOptions } from '../../utils/harbourPupils';
import HarbourPortalShell from './HarbourPortalShell';
import HarbourPupilRecordFlyout from './HarbourPupilRecordFlyout';
import HarbourAddPupilFlyout from './HarbourAddPupilFlyout';
import HarbourAddToSenFlyout from './HarbourAddToSenFlyout';
import HarbourAddToReferralFlyout from './HarbourAddToReferralFlyout';
import HarbourFormsHomeFlyout from './HarbourFormsHomeFlyout';
import HarbourOutcomeLetterFlyout from './HarbourOutcomeLetterFlyout';
import HarbourAddDocumentFlyout from './HarbourAddDocumentFlyout';
import { addedNotice } from '../../utils/referralDocumentPortal';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourSegmented from '../../components/harbour/HarbourSegmented';
import HarbourPupilCard from '../../components/harbour/HarbourPupilCard';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import { HarbourField } from '../../components/harbour/HarbourField';
import '../../components/harbour/harbour.css';

/*
 * Harbour SENCO portal · Your pupils (TDI-53, sections 01-03, 14, and the
 * flyout routes). The page is the hub: the pupil record, Add a pupil, both
 * add-to-pathway wizards and the school-forms homepages all open as flyouts
 * over this list, so closing one always lands back here.
 */
export default function SencoYourPupilsPage({ flyout = null }) {
  const navigate = useNavigate();
  const { patientRecordId, pathwayKey } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;

  const { pupils, loading, completionLoading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const [activeFilter, setActiveFilter] = useState('all');
  const [view, setView] = useState('cards');
  const [panel, setPanel] = useState({ name: '', yearGroup: '' });
  const [panelDraft, setPanelDraft] = useState({ name: '', yearGroup: '' });
  const [panelOpen, setPanelOpen] = useState(false);

  const { counts, visible } = useMemo(() => {
    const base = filterPupilsWithPanel(pupils, activeFilter, {
      yearGroups: panel.yearGroup ? [panel.yearGroup] : [],
    });
    const name = panel.name.trim().toLowerCase();
    const shown = name ? base.visible.filter((p) => p.name.toLowerCase().includes(name)) : base.visible;
    return { ...base, visible: sortPupilsBySurname(shown) };
  }, [pupils, activeFilter, panel]);

  const showSkeleton = loading || (pupils.length > 0 && completionLoading);
  // The panel's filters are out of sight once it closes, so the list says it
  // is filtered and offers the way back to everyone.
  const panelName = panel.name.trim();
  const panelActive = Boolean(panelName || panel.yearGroup);
  const panelSummary = [
    panelName ? `name “${panelName}”` : null,
    panel.yearGroup || null,
  ].filter(Boolean).join(' and ');
  const clearPanel = () => { setPanelDraft({ name: '', yearGroup: '' }); setPanel({ name: '', yearGroup: '' }); };
  const userName = user?.displayName || user?.email || 'Account';
  const activePupil = patientRecordId ? pupils.find((p) => p.id === patientRecordId) : null;
  // Once the list has settled, a pupil still missing from it is not on this
  // school's list at all — a stale bookmark, a pupil who moved, a mistyped id.
  // Without this the flyouts below sat on "Loading pupil record" for good.
  const pupilNotFound = Boolean(patientRecordId) && !loading && !activePupil;

  // While the pathway data is still streaming in, every pupil still looks like
  // "not on a pathway", so the counts would read 0 / 0 / n — wrong, and wrong
  // in a way that looks like an answer. Omitting the count hides the pill.
  const filterItems = [
    { key: 'all', label: 'All pupils', count: showSkeleton ? undefined : counts.all },
    { key: 'sen', label: 'On the SEND pathway', count: showSkeleton ? undefined : counts.sen, pathway: 'sen' },
    { key: 'referral', label: 'On the referral pathway', count: showSkeleton ? undefined : counts.referral, pathway: 'referral' },
    { key: 'none', label: 'Not on a pathway yet', count: showSkeleton ? undefined : counts.none, lockedWhenEmpty: !showSkeleton },
  ];

  // A pupil's form progress is only known once their record snapshot lands.
  const formsLoading = (p) => Boolean((loading || completionLoading) && p.statusUnknown);

  const closeFlyout = () => navigate('/rms/pupils');
  // After a change made from an open record: refresh in place, no loading state.
  const refresh = () => reload({ force: true, silent: true });

  return (
    <HarbourPortalShell userName={userName} userEmail={user?.email} userType={claims?.userType}>
      <div className="flex items-end gap-6">
        <div className="flex flex-col gap-1 min-w-0">
          <h1 className="harbour-heading-32">Your pupils</h1>
          <p className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">
            Add a pupil, then open their pupil record to add them to the SEND pathway or the referral pathway.
          </p>
        </div>
        <div className="ml-auto flex-none">
          <HarbourButton kind="primary" leadingIcon="plus" onClick={() => navigate('/rms/pupils/add')}>
            Add pupil
          </HarbourButton>
        </div>
      </div>

      {/* Hidden while loading (3370:51695, TDI-697): tabs with no counts and a
          view switch over nothing are controls that do nothing yet. */}
      {!showSkeleton && (
      <div className="harbour-list-controls">
        <HarbourSegmented
          ariaLabel="Filter pupils"
          items={filterItems}
          activeKey={activeFilter}
          onChange={setActiveFilter}
        />
        <div className="ml-auto flex items-center gap-3 flex-none">
          <HarbourSegmented
            ariaLabel="View as"
            items={[
              { key: 'cards', label: 'Cards' },
              { key: 'table', label: 'Table' },
            ]}
            activeKey={view}
            onChange={setView}
          />
          <Popover.Root open={panelOpen} onOpenChange={(open) => { setPanelOpen(open); if (open) setPanelDraft(panel); }}>
            <Popover.Trigger asChild>
              <button type="button" className="harbour-btn harbour-btn--secondary harbour-btn--icon-leading">
                <HarbourIcon name="filter" size={20} />
                Filters
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content align="end" sideOffset={8} className="harbour harbour-popover z-50">
                <p className="harbour-title-20">Filters</p>
                <HarbourField
                  label="Search by name"
                  value={panelDraft.name}
                  onChange={(e) => setPanelDraft((d) => ({ ...d, name: e.target.value }))}
                />
                <HarbourField
                  as="select"
                  label="Year group"
                  value={panelDraft.yearGroup}
                  onChange={(e) => setPanelDraft((d) => ({ ...d, yearGroup: e.target.value }))}
                >
                  <option value="">All year groups</option>
                  {yearGroupOptions(pupils).map((yg) => <option key={yg} value={yg}>{yg}</option>)}
                </HarbourField>
                <div className="harbour-popover__footer">
                  <HarbourTextLink onClick={() => { clearPanel(); setPanelOpen(false); }}>
                    Clear all
                  </HarbourTextLink>
                  <HarbourButton kind="primary" onClick={() => { setPanel(panelDraft); setPanelOpen(false); }}>
                    Show pupils
                  </HarbourButton>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      </div>
      )}

      {!showSkeleton && !error && panelActive && (
        <p className="harbour-body-16 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>{`Showing ${visible.length} of ${counts.all} pupils, filtered by ${panelSummary}.`}</span>
          <HarbourTextLink onClick={clearPanel}>Clear filters</HarbourTextLink>
        </p>
      )}

      {error ? (
        <div className="flex flex-col gap-4 max-w-[760px]">
          <HarbourNotice kind="error" title="We could not load this page right now">
            Check your connection, then try again.
          </HarbourNotice>
          <div>
            <HarbourButton kind="secondary" onClick={() => reload()}>Try again</HarbourButton>
          </div>
        </div>
      ) : showSkeleton ? (
        <div className="harbour-skeleton-bars animate-pulse" aria-hidden="true">
          <div className="harbour-skeleton" />
          <div className="harbour-skeleton" />
          <div className="harbour-skeleton" />
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          filtered={activeFilter !== 'all' || Boolean(panel.name || panel.yearGroup)}
          onAddPupil={() => navigate('/rms/pupils/add')}
          onShowAll={() => { setActiveFilter('all'); setPanel({ name: '', yearGroup: '' }); }}
        />
      ) : view === 'cards' ? (
        // Cards 16 apart: three 536 cards fill the 1640 frame (3192:111089).
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((pupil) => (
            <HarbourPupilCard
              key={pupil.id}
              pupil={pupil}
              recordTo={`/rms/pupils/${pupil.id}`}
              onAddSen={!pupil.sen && isSenPathwayEligible(pupil) ? () => navigate(`/rms/pupils/${pupil.id}/add-to-sen`) : undefined}
              onAddReferral={
                !pupil.referral && pupil.patient?.harbour?.pupil
                  ? () => navigate(`/rms/pupils/${pupil.id}/refer`)
                  : undefined
              }
            />
          ))}
        </div>
      ) : (
        <PupilTable
          pupils={visible}
          navigate={navigate}
          onAddSen={(pupil) => navigate(`/rms/pupils/${pupil.id}/add-to-sen`)}
          onAddReferral={(pupil) => navigate(`/rms/pupils/${pupil.id}/refer`)}
        />
      )}

      <p className="sr-only" role="status">
        {showSkeleton ? 'Loading your pupils' : `Showing ${visible.length} of ${counts.all} pupils`}
      </p>

      {/* ── Flyout routes over the list ─────────────────────────────────── */}
      {flyout === 'add' && (
        <HarbourAddPupilFlyout orgId={orgId} pupils={pupils} onClose={closeFlyout} onAdded={refresh} />
      )}
      {flyout === 'record' && (
        <HarbourPupilRecordFlyout
          pupil={activePupil}
          orgId={orgId}
          loading={loading && !activePupil}
          notFound={pupilNotFound}
          onClose={closeFlyout}
          onRefresh={refresh}
        />
      )}
      {flyout === 'sen' && activePupil && !activePupil.statusUnknown && (
        // An ineligible pupil (Reception or secondary, TDI-488) gets the
        // flyout's explanation screen rather than a silent redirect.
        <HarbourAddToSenFlyout pupil={activePupil} orgId={orgId} onClose={() => navigate(`/rms/pupils/${activePupil.id}`)} onStarted={refresh} />
      )}
      {flyout === 'refer' && activePupil && !activePupil.statusUnknown && (
        <HarbourAddToReferralFlyout pupil={activePupil} orgId={orgId} onClose={() => navigate(`/rms/pupils/${activePupil.id}`)} onStarted={refresh} />
      )}
      {/* Skeletons only while we are still waiting to hear about this pupil.
          statusUnknown alone would never clear if the record listener errored,
          leaving the page loading for good; both flags here always settle. */}
      {flyout === 'senForms' && activePupil && (
        <HarbourFormsHomeFlyout pupil={activePupil} pathway="sen" orgId={orgId} loading={formsLoading(activePupil)} onClose={() => navigate(`/rms/pupils/${activePupil.id}`)} />
      )}
      {flyout === 'referralForms' && activePupil && (
        <HarbourFormsHomeFlyout pupil={activePupil} pathway="referral" orgId={orgId} loading={formsLoading(activePupil)} onClose={() => navigate(`/rms/pupils/${activePupil.id}`)} />
      )}
      {flyout === 'letter' && activePupil && !activePupil.statusUnknown && (
        <HarbourOutcomeLetterFlyout
          pupil={activePupil}
          pathwayKey={pathwayKey}
          orgId={orgId}
          onClose={() => navigate(`/rms/pupils/${activePupil.id}`)}
        />
      )}
      {flyout === 'addDocument' && activePupil && !activePupil.statusUnknown && (
        // TDI-578: opened from the Documents block on the record; saving
        // goes back to the record with the "has been added" notice.
        <HarbourAddDocumentFlyout
          pupil={activePupil}
          orgId={orgId}
          onClose={() => navigate(`/rms/pupils/${activePupil.id}`)}
          onAdded={(doc) => navigate(`/rms/pupils/${activePupil.id}`, {
            state: { documentNotice: addedNotice(doc && doc.description) },
          })}
        />
      )}
      {(flyout === 'sen' || flyout === 'refer' || flyout === 'letter' || flyout === 'addDocument') && activePupil && activePupil.statusUnknown && (
        // Opened straight from an address before the record has arrived
        // (TDI-807): the record flyout shows its loading state (or its load
        // error if the listener failed) instead of a half-built screen.
        <HarbourPupilRecordFlyout
          pupil={activePupil}
          orgId={orgId}
          loading={false}
          notFound={pupilNotFound}
          onClose={closeFlyout}
          onRefresh={refresh}
        />
      )}
      {(flyout === 'sen' || flyout === 'refer' || flyout === 'senForms' || flyout === 'referralForms' || flyout === 'letter' || flyout === 'addDocument') && !activePupil && (
        <HarbourPupilRecordFlyout
          pupil={null}
          orgId={orgId}
          loading={!pupilNotFound}
          notFound={pupilNotFound}
          onClose={closeFlyout}
          onRefresh={refresh}
        />
      )}
    </HarbourPortalShell>
  );
}

function EmptyState({ filtered, onAddPupil, onShowAll }) {
  return (
    <div className="harbour-card max-w-[760px] flex flex-col items-start gap-4 p-10 rounded-3xl">
      <HarbourIcon name="info" size={24} />
      <div className="flex flex-col gap-2">
        <p className="harbour-title-20">{filtered ? 'No pupils match this view' : 'No pupils yet'}</p>
        <p className="harbour-body-16">
          {filtered
            ? 'No pupils match these filters at the moment.'
            : 'Pupils you add will appear here, with their pathway progress.'}
        </p>
      </div>
      {filtered ? (
        <HarbourButton kind="secondary" onClick={onShowAll}>Show all pupils</HarbourButton>
      ) : (
        <HarbourButton kind="primary" leadingIcon="plus" onClick={onAddPupil}>Add pupil</HarbourButton>
      )}
    </div>
  );
}

// A row whose referral lines carry the links (one per pathway).
function hasReferralLinks(pupil) {
  return Boolean(pupil.referral) && (pupil.referral.rows || []).some((row) => row.emphasis);
}

function PupilTable({ pupils, navigate, onAddSen, onAddReferral }) {
  return (
    <div className="harbour-card rounded-3xl px-6">
      <table className="w-full border-collapse">
        <thead>
          <tr className="text-left border-b border-[color:var(--harbour-colour-line-default)]">
            <th scope="col" className="py-3 pr-6 w-[280px] harbour-body-16-semibold text-[color:var(--harbour-colour-text-muted)] font-semibold">
              Pupil
            </th>
            <th scope="col" className="py-3 pr-6 harbour-body-16-semibold text-[color:var(--harbour-colour-text-muted)] font-semibold">
              <span className="inline-flex items-center gap-2">
                <HarbourPathwayMark pathway="sen" />
                SEND pathway
              </span>
            </th>
            <th scope="col" className="py-3 harbour-body-16-semibold text-[color:var(--harbour-colour-text-muted)] font-semibold">
              <span className="inline-flex items-center gap-2">
                <HarbourPathwayMark pathway="referral" />
                Referral pathway
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {pupils.map((pupil) => (
            <tr key={pupil.id} className="border-b last:border-b-0 border-[color:var(--harbour-colour-line-default)] align-top">
              <td className="py-4 pr-6">
                <div className="flex flex-col">
                  {/* With a referral, ADHD and Autism are the row's links and
                      the name is plain bold (3192:111158, TDI-694); a row
                      with no referral keeps its name as the link. */}
                  {hasReferralLinks(pupil) ? (
                    <span className="harbour-table-pupil-name">{pupil.name}</span>
                  ) : (
                    <button
                      type="button"
                      className="harbour-pupil-card__name bg-transparent border-0 p-0 cursor-pointer text-left"
                      onClick={() => navigate(`/rms/pupils/${pupil.id}`)}
                    >
                      {pupil.name}
                    </button>
                  )}
                  {pupil.yearGroup && (
                    <span className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">{pupil.yearGroup}</span>
                  )}
                </div>
              </td>
              <td className="py-4 pr-6">
                {pupil.sen ? (
                  <div className="harbour-table-cell max-w-[632px]">
                    <span className="harbour-table-cell__stage harbour-lane__stage harbour-lane__stage--sen">
                      {pupil.sen.stageLabel}
                    </span>
                    <div className="harbour-table-cell__rows">
                      {pupil.sen.rows.map((row) => (
                        <div key={row.key} className="harbour-lane__row">
                          {/* An empty label would still hold its 160px
                              column and push the chip away from the stage. */}
                          {row.label ? <span className="harbour-lane__row-label">{row.label}</span> : null}
                          <HarbourStatusChip status={row.chip.status} label={row.chip.label} tone={row.chip.tone || 'neutral'} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="harbour-table-cell__not-active max-w-[632px]">
                    <span>Not active</span>
                    {isSenPathwayEligible(pupil) && (
                      <HarbourTextLink leadingIcon="plus" onClick={() => onAddSen(pupil)}>
                        Add to SEND pathway
                      </HarbourTextLink>
                    )}
                  </div>
                )}
              </td>
              <td className="py-4">
                {pupil.referral ? (
                  <div className="harbour-table-cell__rows max-w-[632px]">
                    {pupil.referral.rows.map((row) => (
                      <div key={row.key} className="harbour-lane__row">
                        {row.emphasis ? (
                          <Link
                            to={`/rms/pupils/${pupil.id}`}
                            className="harbour-lane__row-name harbour-table-referral-link"
                            aria-label={`${pupil.name}'s ${row.pathwayKey === 'asd' ? 'autism' : row.label} referral`}
                          >
                            {row.label}
                          </Link>
                        ) : (
                          <span className="harbour-lane__row-label">{row.label}</span>
                        )}
                        <HarbourStatusChip status={row.chip.status} label={row.chip.label} tone={row.chip.tone || 'referral'} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="harbour-table-cell__not-active max-w-[632px]">
                    <span>Not active</span>
                    {pupil.patient?.harbour?.pupil && (
                      <HarbourTextLink leadingIcon="plus" onClick={() => onAddReferral(pupil)}>
                        Add to referral pathway
                      </HarbourTextLink>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
